'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { DashboardLayout } from '@/components/dashboard-layout'
import { Badge } from '@/components/badge'
import { StatCard } from '@/components/stat-card'
import {
  Play,
  Square,
  RotateCw,
  Trash2,
  FileText,
  Activity,
  AlertCircle,
  CheckCircle2,
  Server,
} from 'lucide-react'

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

import type { Container } from '@/types/container'
import * as api from '@/lib/api'

/* ----------------------------- Helpers ----------------------------- */

function formatUptime(created: number): string {
  const now = Date.now() / 1000
  const diff = now - created
  const days = Math.floor(diff / 86400)
  const hours = Math.floor((diff % 86400) / 3600)

  if (days > 0) return `${days}d ${hours}h`
  return `${hours}h`
}

function formatCreated(created: number): string {
  return new Date(created * 1000).toLocaleString()
}

/* -------------------------- Status Badge --------------------------- */

function StatusBadge({ state }: { state: string }) {
  if (state === 'running') return <Badge variant="success">RUNNING</Badge>
  if (state === 'exited') return <Badge variant="warning">STOPPED</Badge>
  return <Badge variant="error">ERROR</Badge>
}

/* -------------------------- Logs Modal ----------------------------- */

function LogViewerModal({
  containerId,
  onClose,
}: {
  containerId: string
  onClose: () => void
}) {
  const [logs, setLogs] = useState<string>('Loading...')

  useEffect(() => {
    api.getLogs(containerId)
      .then((data) => setLogs(data.logs || 'No logs'))
      .catch(() => setLogs('Failed to load logs'))
  }, [containerId])

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between p-4 border-b border-border">
          <h3 className="font-semibold">Container Logs</h3>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-black/40 font-mono text-xs text-green-400">
          <pre className="whitespace-pre-wrap">{logs}</pre>
        </div>
      </div>
    </div>
  )
}

/* ============================= PAGE ============================= */

export default function HoneypotsPage() {
  const router = useRouter()

  const [containers, setContainers] = useState<Container[]>([])
  const [loading, setLoading] = useState(true)
  const [logViewerId, setLogViewerId] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  /* ----------------------- Fetch Containers ----------------------- */

  const fetchContainers = async () => {
    try {
      const data = await api.getContainers()
      if (Array.isArray(data)) setContainers(data)
    } catch (err) {
      console.error('Fetch containers failed', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContainers()
    const interval = setInterval(fetchContainers, 5000)
    return () => clearInterval(interval)
  }, [])

  /* ----------------------- Actions ----------------------- */

  const handleAction = async (id: string, action: string) => {
    setActionLoading(id)

    try {
      if (action === 'delete') {
        await api.deleteContainer(id)
      } else if (action === 'start') {
        await api.startContainer(id)
      } else if (action === 'stop') {
        await api.stopContainer(id)
      } else if (action === 'restart') {
        await api.restartContainer(id)
      }

      await fetchContainers()
    } catch (err) {
      console.error('Action failed', err)
    } finally {
      setActionLoading(null)
    }
  }

  /* ----------------------- Stats ----------------------- */

  const running = containers.filter((c) => c.State === 'running').length
  const stopped = containers.filter((c) => c.State === 'exited').length
  const errors = containers.filter(
    (c) => !['running', 'exited'].includes(c.State)
  ).length

  /* ----------------------- Charts Mock ----------------------- */

  const activityData = [
    { time: '00:00', attacks: 120, connections: 85 },
    { time: '04:00', attacks: 89, connections: 62 },
    { time: '08:00', attacks: 234, connections: 156 },
    { time: '12:00', attacks: 312, connections: 210 },
    { time: '16:00', attacks: 445, connections: 298 },
    { time: '20:00', attacks: 567, connections: 412 },
    { time: '23:59', attacks: 389, connections: 285 },
  ]

  const typeDistribution = [
    { name: 'Cowrie', value: 847, fill: '#6d5acd' },
    { name: 'Dionaea', value: 2341, fill: '#8b9ef8' },
    { name: 'SMB', value: 1203, fill: '#818cf8' },
  ]

  /* ======================== UI ======================== */

  return (
    <DashboardLayout
      title="Managed Honeypots"
      subtitle={`Total orchestration of ${containers.length} containers`}
      actions={
        <button
          onClick={() => router.push('/honeypots/deploy')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
        >
          + Deploy Container
        </button>
      }
    >
      {logViewerId && (
        <LogViewerModal
          containerId={logViewerId}
          onClose={() => setLogViewerId(null)}
        />
      )}

      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={containers.length} icon={Server} />
          <StatCard label="Running" value={running} icon={Activity} />
          <StatCard label="Stopped" value={stopped} icon={CheckCircle2} />
          <StatCard label="Errors" value={errors} icon={AlertCircle} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-card border rounded-lg p-6">
            <h3 className="mb-4 font-semibold">Attack Activity</h3>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="attacks" fill="#6d5acd" />
                <Bar dataKey="connections" fill="#818cf8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card border rounded-lg p-6">
            <h3 className="mb-4 font-semibold">Distribution</h3>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={typeDistribution} dataKey="value">
                  {typeDistribution.map((e, i) => (
                    <Cell key={i} fill={e.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="bg-card border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Image</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Ports</th>
                  <th className="p-3">Uptime</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {containers.map((c) => {
                  const name = c.Names?.[0]?.replace('/', '') || 'unknown'

                  const ports =
                    c.Ports?.map((p) =>
                      p.PublicPort
                        ? `${p.PublicPort}:${p.PrivatePort}`
                        : p.PrivatePort
                    ).join(', ') || '-'

                  return (
                    <tr key={c.Id} className="border-b hover:bg-secondary/30">
                      <td className="p-3">{name}</td>
                      <td className="p-3 text-muted-foreground">{c.Image}</td>
                      <td className="p-3">
                        <StatusBadge state={c.State} />
                      </td>
                      <td className="p-3">{ports}</td>
                      <td className="p-3">
                        {formatUptime(c.Created)}
                      </td>

                      <td className="p-3 flex gap-2 justify-end">
                        {c.State !== 'running' && (
                          <button
                            onClick={() => handleAction(c.Id, 'start')}
                          >
                            <Play size={16} />
                          </button>
                        )}

                        {c.State === 'running' && (
                          <>
                            <button
                              onClick={() => handleAction(c.Id, 'stop')}
                            >
                              <Square size={16} />
                            </button>

                            <button
                              onClick={() => handleAction(c.Id, 'restart')}
                            >
                              <RotateCw size={16} />
                            </button>
                          </>
                        )}

                        <button onClick={() => setLogViewerId(c.Id)}>
                          <FileText size={16} />
                        </button>

                        <button
                          onClick={() => handleAction(c.Id, 'delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}